import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Plus } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { gauntlet, ApiError } from '../lib/api';
import { cx } from '../lib/format';
import { Badge, Button, Callout, ChainAvatar, Input, Panel, Skeleton } from '../components/ui';

const TIERS = [
  { id: 'all', label: 'All' },
  { id: 'full', label: 'Send + receive' },
  { id: 'watch', label: 'Receive + track' },
  { id: 'derive', label: 'Address only' },
] as const;

/**
 * The chain browser.
 *
 * Support tier is stated plainly on every row. Claiming "300+ chains supported"
 * while quietly meaning "we can print an address for them" is the sort of thing
 * that loses people money, so the distinction is front and centre.
 */
export function ChainsPage() {
  const [search, setSearch] = useState('');
  const [tier, setTier] = useState<(typeof TIERS)[number]['id']>('all');
  const [showTestnets, setShowTestnets] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  /** What this wallet already has, so the row shows state instead of offering a duplicate. */
  const { data: walletData } = useQuery({
    queryKey: ['wallets'],
    queryFn: () => gauntlet.wallets(),
  });

  const wallet = walletData?.wallets.find((w) => w.type === 'hd') ?? walletData?.wallets[0];
  const added = new Set(wallet?.accounts.map((a) => a.chain_id) ?? []);

  const addChain = useMutation({
    mutationFn: (chainId: string) => gauntlet.addAccount(wallet!.id, chainId),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries();
    },
    onError: (err) =>
      setError(
        err instanceof ApiError
          ? err.isLocked
            ? 'Unlock your wallet first — adding a chain derives new keys.'
            : err.message
          : 'Could not add that chain.',
      ),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['chains'],
    queryFn: () => gauntlet.chains(),
    staleTime: Infinity,
  });

  const filtered = useMemo(() => {
    const needle = search.toLowerCase();
    return (data?.chains ?? []).filter((chain) => {
      if (!showTestnets && chain.testnet) return false;
      if (tier !== 'all' && chain.tier !== tier) return false;
      if (!needle) return true;
      return (
        chain.name.toLowerCase().includes(needle) ||
        chain.symbol.toLowerCase().includes(needle) ||
        chain.id.includes(needle)
      );
    });
  }, [data, search, tier, showTestnets]);

  if (isLoading) return <Skeleton className="h-96 w-full" />;

  const stats = data?.stats;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Supported chains</h1>
        <p className="mt-1 text-sm text-ink-dim">
          Add a network to your wallet to get an address for it. {stats?.total} available — {stats?.byTier['full']} with full send support,{' '}
          {stats?.byTier['watch']} tracked, {stats?.byTier['derive']} address-derivation only.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <Stat label="Total chains" value={String(stats?.total ?? 0)} />
        <Stat label="EVM networks" value={String(stats?.byFamily['evm'] ?? 0)} />
        <Stat label="UTXO chains" value={String(stats?.byFamily['utxo'] ?? 0)} />
        <Stat label="Cosmos zones" value={String(stats?.byFamily['cosmos'] ?? 0)} />
      </div>

      {error && <Callout tone="danger">{error}</Callout>}

      {wallet && !wallet.unlocked && (
        <Callout tone="info" title="Unlock your wallet to add chains">
          Adding a network derives new keys, which needs the vault open. Open{' '}
          <strong>{wallet.name}</strong> from the sidebar and unlock it first.
        </Callout>
      )}

      {!wallet && (
        <Callout tone="warn" title="No wallet yet">
          Create a wallet from the dashboard first — chains are added to a wallet.
        </Callout>
      )}

      <Panel padded={false}>
        <div className="space-y-3 border-b border-hairline p-4">
          <Input
            placeholder="Search by name, ticker or id…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="flex flex-wrap items-center gap-1.5">
            {TIERS.map((entry) => (
              <button
                key={entry.id}
                onClick={() => setTier(entry.id)}
                className={cx(
                  'rounded-md border px-2.5 py-1 text-xs transition-colors',
                  tier === entry.id
                    ? 'border-brass/50 bg-brass-wash text-brass'
                    : 'border-hairline text-ink-dim hover:border-hairline-bright',
                )}
              >
                {entry.label}
              </button>
            ))}
            <label className="ml-auto flex cursor-pointer items-center gap-1.5 text-xs text-ink-dim">
              <input
                type="checkbox"
                checked={showTestnets}
                onChange={(e) => setShowTestnets(e.target.checked)}
                className="accent-[var(--color-brass)]"
              />
              Show testnets
            </label>
          </div>
        </div>

        <ul className="divide-y divide-hairline">
          {filtered.map((chain) => (
            <li key={chain.id} className="flex items-center gap-3 px-4 py-3">
              <ChainAvatar chainId={chain.id} symbol={chain.symbol} size={30} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="truncate text-sm font-medium">{chain.name}</span>
                  {chain.testnet && <Badge tone="warn">testnet</Badge>}
                  {chain.l2 && <Badge>L2</Badge>}
                </div>
                <p className="text-xs text-ink-faint">
                  {chain.symbol} · {chain.family}
                  {chain.chainId ? ` · chain ${chain.chainId}` : ''} · SLIP-44 {chain.slip44}
                </p>
              </div>
              <Badge
                tone={chain.tier === 'full' ? 'gain' : chain.tier === 'watch' ? 'brass' : 'neutral'}
              >
                {chain.tier === 'full'
                  ? 'send + receive'
                  : chain.tier === 'watch'
                    ? 'receive + track'
                    : 'address only'}
              </Badge>

              {/* The control belongs on the page called "Chains". It was
                  previously only inside the wallet detail screen, which is not
                  where anyone looks for it. */}
              {added.has(chain.id) ? (
                <span className="inline-flex shrink-0 items-center gap-1 text-xs text-gain">
                  <Check size={13} /> added
                </span>
              ) : (
                <Button
                  size="sm"
                  icon={<Plus size={13} />}
                  disabled={!wallet}
                  loading={addChain.isPending && addChain.variables === chain.id}
                  onClick={() => addChain.mutate(chain.id)}
                >
                  Add
                </Button>
              )}
            </li>
          ))}
          {filtered.length === 0 && (
            <li className="px-4 py-10 text-center text-sm text-ink-faint">No chains match.</li>
          )}
        </ul>
      </Panel>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="panel p-4">
      <p className="label-xs">{label}</p>
      <p className="num mt-1 text-2xl font-semibold">{value}</p>
    </div>
  );
}
